import { Group, Title, Badge, Text } from '@mantine/core';
import {
  formatSearchQuery,
  parseSearchRequest,
  SearchRequest,
  formatHumanName,
} from '@medplum/core';
import { Document, useMedplum, useMedplumNavigate } from '@medplum/react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import { SearchControl } from '@medplum-components/SearchControl/SearchControl';
import { FC, useEffect, useState } from 'react';
import { addDefaultSearchValues } from '@utils/search-utils';
import CreatePractitionerModal from '../pages/ainigma/CreatePractitionerModal';
import { Practitioner } from '@medplum/fhirtypes';
import { ARKON } from '@consts/systems.consts';

// -----------------------------------------------------------------------------
export const PractitionersPage: FC = () => {
  const medplum = useMedplum();
  const navigate = useMedplumNavigate();
  const location = useLocation();
  const { t } = useTranslation('practitioners');

  const [search, setSearch] = useState<SearchRequest>({
    resourceType: 'Practitioner',
  });

  // ---------------------------------------------------------------------------
  const renderPractitionerCell = (resource: any, field: { name: string }) => {
    if (resource.resourceType === 'Practitioner') {
      const practitioner = resource as Practitioner;

      // Name column — just render the name
      if (field.name === 'name') {
        const name = formatHumanName(practitioner.name?.[0]);

        return (
          <Group gap="xs" align="center">
            {name}
          </Group>
        );
      }

      // For organization field, show a single badge (no duplicate text)
      if (field.name === 'organization') {
        const organizationExtension = practitioner.extension?.find(
          (ext) => ext.url === ARKON.SD.EXTERNAL_ORGANIZATION_EXTENSION
        );

        const isExternal = !!organizationExtension?.valueString;
        const label = isExternal
          ? (organizationExtension?.valueString ?? t('page.external'))
          : t('page.internal');

        return (
          <Badge
            size="md"
            color={isExternal ? 'orange' : 'blue'}
            variant="light"
          >
            {label}
          </Badge>
        );
      }

      if (field.name === 'specialty') {
        const specialties =
          practitioner?.qualification?.[0]?.code?.coding?.[0]?.display || '';
        return <Text>{specialties}</Text>;
      }
    }

    return undefined;
  };

  // ---------------------------------------------------------------------------
  useEffect(() => {
    const parsedSearch = parseSearchRequest(
      location.pathname + location.search
    );
    const populatedSearch = addDefaultSearchValues(
      {
        ...parsedSearch,
        resourceType: 'Practitioner',
      },
      ['name', 'organization', 'specialty', '_lastUpdated']
    );

    if (location.search === formatSearchQuery(populatedSearch)) {
      setSearch(populatedSearch);
    } else {
      navigate(`/practitioners${formatSearchQuery(populatedSearch)}`);
    }
  }, [medplum, navigate, location]);

  // ---------------------------------------------------------------------------
  return (
    <Document>
      <Title>
        <Group justify="space-between">
          {t('page.title')}
          <CreatePractitionerModal />
        </Group>
      </Title>
      <SearchControl
        search={search}
        onClick={(e) => navigate(`/Practitioner/${e.resource.id}`)}
        onChange={(e) => {
          navigate(`/practitioners${formatSearchQuery(e.definition)}`);
        }}
        renderCell={renderPractitionerCell}
        hideToolbar
      />
      <Outlet />
    </Document>
  );
};
